import React, { useState, useEffect, useCallback, useContext } from "react";
import {
	Box,
	Divider,
	IconButton,
	Badge,
	ListItemButton,
	ListItemAvatar,
	Avatar,
	ListItemText,
	ListItemSecondaryAction,
	CssBaseline,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import { useLocation } from "react-router-dom";
import { useSnackbar } from "notistack";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";
import NotificationIcon from "@mui/icons-material/NotificationsOutlined";
import ExpandMore from "@mui/icons-material/ExpandMore";

import { UserMenu } from "./UserMenu";
import { NavigationDrawer } from "./NavigationDrawer";
import Search from "@components/search";
import { MaterialUISwitch } from "@/components/Switch";
import Customizer from "@/components/customize/customizer";
import { ColorModeContext } from "@/App";
import { fetchUser } from "@/hooks/acl";
import { User, Notification } from "@/types/user";
import type { Navigation } from "@/types/navigation";
import { DRAWER_WIDTH, DRAWER_WIDTH_COLLAPSED, HEADER_HEIGHT } from "./config";

export const drawerWidth = DRAWER_WIDTH;
export const drawerWidthCollapsed = DRAWER_WIDTH_COLLAPSED;
export const headerHeight = HEADER_HEIGHT;

const MainLayout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
	const [isNavOpen, setIsNavOpen] = useState(
		localStorage.getItem("isNavOpen") === "true",
	);
	const [user, setUser] = useState<User | null>(null);
	const [notifications, setNotifications] = useState<Notification[]>([]);
	const [navigations, setNavigations] = useState<Navigation[]>([]);
	const [loading, setLoading] = useState(true);
	const [changingDesign, setChangingDesign] = useState(false);
	const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
	const [notificationAnchorEl, setNotificationAnchorEl] =
		useState<null | HTMLElement>(null);

	const theme = useTheme();
	const location = useLocation();
	const snackbar = useSnackbar();
	const colorMode = useContext(ColorModeContext);

	const getUser = useCallback(() => {
		fetchUser()
			.then((data) => {
				setUser(data);
				setNavigations(data.navigations);
				setNotifications(data.notifications);
			})
			.catch((error) => {
				console.error(error);
				snackbar.enqueueSnackbar("Failed to fetch user data", {
					variant: "error",
				});
			})
			.finally(() => setLoading(false));
	}, [snackbar]);

	useEffect(() => {
		getUser();
		const interval = setInterval(getUser, 60000);
		return () => clearInterval(interval);
	}, [getUser]);

	const handleNotificationOpen = () => {
		// ... (implementation for opening notifications)
	};

	return (
		<Box sx={{ display: "flex" }}>
			<CssBaseline />
			<UserMenu
				anchorEl={anchorEl}
				onClose={() => setAnchorEl(null)}
				onChangingDesign={() => setChangingDesign(true)}
				remainingSessionTime={user?.sessionTimeRemaining}
			/>
			{changingDesign && (
				<Customizer
					open={changingDesign}
					onClose={() => setChangingDesign(false)}
					onSuccess={() => window.location.reload()}
				/>
			)}
			<NavigationDrawer
				open={isNavOpen}
				headerHeight={headerHeight}
				navigations={navigations}
				activePath={location.pathname}
			/>
			<Box
				component="main"
				sx={{
					display: "flex",
					flexDirection: "column",
					flexGrow: 1,
					minWidth: 0,
					//marginLeft: isNavOpen ? `${drawerWidth}px` : `${drawerWidthCollapsed}px`,
					transition: theme.transitions.create("margin", {
						easing: theme.transitions.easing.sharp,
						duration: theme.transitions.duration.leavingScreen,
					}),
				}}
			>
				<Box
					id="header"
					sx={{
						display: "flex",
						justifyContent: "space-between",
						alignItems: "center",
						height: headerHeight,
						px: 3,
						position: "sticky",
						top: 0,
						zIndex: theme.zIndex.appBar,
						backgroundColor: alpha(theme.palette.background.default, 0.5),
						backdropFilter: "blur(10px)",
						borderBottom: `1px solid ${theme.palette.divider}`,
					}}
				>
					<Box sx={{ display: "flex", alignItems: "center", gap: 2, flex: 1 }}>
						<IconButton onClick={() => setIsNavOpen(!isNavOpen)} edge="start">
							{isNavOpen ? <CloseIcon /> : <MenuIcon />}
						</IconButton>
						<Search placeholder="Хайлт хийх... (Ctrl+K)" width="half" />
					</Box>
					<Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
						<MaterialUISwitch onChange={colorMode.toggleColorMode} />
						<IconButton size="large" onClick={handleNotificationOpen}>
							<Badge
								badgeContent={notifications.filter((n) => !n.readAt).length}
								color="primary"
							>
								<NotificationIcon />
							</Badge>
						</IconButton>
						<ListItemButton
							dense
							sx={{
								borderRadius: 3,
								maxWidth: 300,
								width: { xs: "auto", md: 300 },
							}}
							onClick={(e) => setAnchorEl(e.currentTarget)}
						>
							<ListItemAvatar>
								<Avatar
									src={user?.avatar ?? "https://placehold.co/150x150"}
									alt="user"
								/>
							</ListItemAvatar>
							<Box sx={{ display: { xs: "none", md: "block" } }}>
								<ListItemText
									primary={user?.firstName}
									secondary={"системхэрэглэгч"}
								/>
							</Box>
							<ListItemSecondaryAction
								sx={{ display: { xs: "none", md: "block" } }}
							>
								<ExpandMore />
							</ListItemSecondaryAction>
						</ListItemButton>
					</Box>
				</Box>
				<Divider />
				<Box sx={{ p: 3, backgroundColor: "background.default", flexGrow: 1 }}>
					{children}
				</Box>
			</Box>
		</Box>
	);
};

export default MainLayout;
